import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ShieldCheck, User, Mail, Crown, Wallet, FileText, Trash2, Pencil, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  'terms-of-service': 'Terms of Service',
  'privacy-policy': 'Privacy Policy',
  'cookie-policy': 'Cookie Policy',
  'earnings-disclaimer': 'Earnings Disclaimer',
  'refund-policy': 'Refund Policy',
  'member-agreement': 'Member Agreement',
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-xs text-foreground text-right break-all">{value || '—'}</span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-white/[0.03] border border-border rounded-lg p-4" data-testid={testId}>
    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-gold" /> {title}
    </h3>
    {children}
  </div>
);

// Member privacy centre — shows what data the platform holds and lets the
// member request changes or account deletion (manual admin review).
export default function PrivacyCenter({ onEditProfile }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/privacy/my-data`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submitDeletion = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/privacy/deletion-request`, { reason }, { headers: getAuthHeader() });
      toast.success('Deletion request submitted — an administrator will review it.');
      setShowDelete(false);
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="wm-card p-6 rounded-lg text-muted-foreground text-sm">Loading your data…</div>;
  }
  if (!data) {
    return <div className="wm-card p-6 rounded-lg text-muted-foreground text-sm">Could not load your data.</div>;
  }

  const del = data.deletion_request;
  const fin = data.financial;

  return (
    <div className="space-y-6" data-testid="privacy-center">
      <div className="wm-card p-6 rounded-lg">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-gold" /> Your data &amp; privacy
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          This is everything we hold in your member profile. You can update your details anytime,
          or request that your account and data be deleted — deletion requests are reviewed by an
          administrator.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <SectionCard icon={User} title="Profile" testId="privacy-section-profile">
            <Row label="Full name" value={data.profile.name} />
            <Row label="Display name" value={data.profile.display_name} />
            <Row label="Email" value={data.profile.email} />
            <Row label="Phone" value={data.profile.phone} />
            <Row label="Specialty" value={data.profile.specialty} />
            <Row label="Profile photo" value={data.profile.profile_photo ? 'On file' : 'None'} />
          </SectionCard>
          <SectionCard icon={Mail} title="Public contact settings" testId="privacy-section-contact">
            <Row label="Public email" value={data.contact.contact_email} />
            <Row label="Public phone" value={data.contact.contact_phone} />
            <Row label="Lead webhook" value={data.contact.lead_notification_webhook ? 'Configured' : 'Not set'} />
          </SectionCard>
          <SectionCard icon={Crown} title="Membership" testId="privacy-section-membership">
            <Row label="Tier" value={data.membership.tier} />
            <Row label="Subscription" value={data.membership.subscription_status} />
            <Row label="Referral code" value={data.membership.referral_code} />
            <Row label="Member since" value={data.membership.member_since ? new Date(data.membership.member_since).toLocaleDateString() : ''} />
          </SectionCard>
          <SectionCard icon={Wallet} title="Financial" testId="privacy-section-financial">
            <Row label="Stripe payouts" value={data.financial.stripe_connect_status.replace('_', ' ')} />
            <Row label="Total earned" value={`$${(fin.total_profit_earned + fin.total_downline_commission).toFixed(2)}`} />
            <Row label="Total paid out" value={`$${fin.total_paid_out.toFixed(2)}`} />
          </SectionCard>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <Button onClick={onEditProfile} variant="outline" className="border-border" data-testid="privacy-edit-profile-btn">
            <Pencil className="w-4 h-4 mr-2" /> Update my information
          </Button>
          {!del || del.status === 'denied' ? (
            <Button
              variant="outline"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
              onClick={() => setShowDelete(!showDelete)}
              data-testid="privacy-request-deletion-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Request account deletion
            </Button>
          ) : null}
        </div>

        {del && (
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
              del.status === 'pending'
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                : del.status === 'approved'
                ? 'border-red-500/30 bg-red-500/10 text-red-400'
                : 'border-white/10 bg-white/[0.03] text-muted-foreground'
            }`}
            data-testid="privacy-deletion-status"
          >
            Deletion request {del.status} — submitted {new Date(del.created_at).toLocaleDateString()}
            {del.note ? ` · Admin note: ${del.note}` : ''}
          </div>
        )}

        {showDelete && (
          <div className="mt-4 border border-red-500/30 rounded-lg p-4 bg-red-500/5" data-testid="privacy-deletion-form">
            <p className="text-sm text-foreground font-medium">Request account deletion</p>
            <p className="text-xs text-muted-foreground mt-1">
              An administrator will review your request (outstanding balances and commissions are
              settled first). This does not delete your account immediately.
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional — tell us why you're leaving"
              rows={2}
              className="mt-3 bg-input border-border text-foreground"
              data-testid="privacy-deletion-reason"
            />
            <div className="flex gap-2 mt-3">
              <Button
                onClick={submitDeletion}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="privacy-deletion-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Submit request
              </Button>
              <Button variant="outline" className="border-border" onClick={() => setShowDelete(false)} data-testid="privacy-deletion-cancel">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="wm-card p-6 rounded-lg" data-testid="privacy-consent-history">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-gold" /> Your policy consents
        </h2>
        {data.consents.legal.length === 0 ? (
          <p className="text-sm text-muted-foreground">No policy acceptances on file yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Document</th><th className="py-2">Version</th><th className="py-2">Accepted</th>
                </tr>
              </thead>
              <tbody>
                {data.consents.legal.map((a, i) => (
                  <tr key={i} className="border-b border-border/30">
                    <td className="py-2 text-foreground">{DOC_LABELS[a.doc_slug] || a.doc_slug}</td>
                    <td className="py-2 text-muted-foreground">v{a.version}</td>
                    <td className="py-2 text-muted-foreground">{new Date(a.accepted_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.consents.cookies && (
          <p className="text-xs text-muted-foreground mt-3" data-testid="privacy-cookie-consent-line">
            Cookie consent: {data.consents.cookies.action.replace('_', ' ')} (analytics{' '}
            {data.consents.cookies.analytics ? 'on' : 'off'}, marketing{' '}
            {data.consents.cookies.marketing ? 'on' : 'off'}) — recorded{' '}
            {new Date(data.consents.cookies.created_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
